// ✅ src/pages/VehicleV2/BatterySupplierDetailReport.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/batterySupplierDetailsReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const fmtDateDDMMYYYY = (v) => {
  if (!v) return "";
  const s = toStr(v);
  if (s.includes("/")) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const formatMoney = (v) =>
  toNum(v, 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function BatterySupplierDetailReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend-matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  // dropdown
  const [suppliers, setSuppliers] = useState([]);

  // filters
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    supplier: "Select",
    serialNo: "",
  });

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ---------------- UI classes ---------------- */
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- dropdowns ---------------- */
  const loadDropdowns = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/suppliers`, {
        headers: apiHeaders,
        params: {
          branchType: sessionBranchType,
          branchId: sessionBranchId,
          loginUserId: userId,
          finYearId,
          finYear,
        },
      });
      setSuppliers(data?.rows || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Dropdown load failed"
      );
    }
  };

  /* ---------------- list ---------------- */
  const loadList = async (f = filters) => {
    try {
      setLoading(true);

      const params = {
        branchType: sessionBranchType,
        branchId: sessionBranchId,
        loginUserId: userId,
        finYearId,
        finYear,
        fromDate: f.fromDate || "",
        toDate: f.toDate || "",
        supplier: isSelect(f.supplier) ? "" : f.supplier,
        serialNo: toStr(f.serialNo),
      };

      const { data } = await axios.get(`${API_BASE}/list`, {
        headers: apiHeaders,
        params,
      });

      setRows(data?.rows || []);
      setCurrentPage(1);
      toast.success(`Found ${(data?.rows || []).length} records`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "List load failed"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadList(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiHeaders]);

  /* ---------------- events ---------------- */
  const handleChange = (e) =>
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSearch = () => loadList(filters);

  const handleViewAll = () => {
    const reset = { fromDate: "", toDate: "", supplier: "Select", serialNo: "" };
    setFilters(reset);
    loadList(reset);
    toast.info("Showing all records");
  };

  const handleClearFilters = () => {
    setFilters({ fromDate: "", toDate: "", supplier: "Select", serialNo: "" });
    toast.info("Filters cleared");
  };

  const handlePrintAll = () => {
    navigate("/vehicle/BatterySupplierDetailReport_Print", {
      state: {
        finYearId,
        finYear,
        branchType: sessionBranchType,
        branchId: sessionBranchId,
        loginUserId: userId,
        filters,
        currentPage,
        itemsPerPage,
        totalRecords: rows.length,
        printScope: "ALL",
      },
    });
  };

  const handlePrintCurrentPage = () => {
    navigate("/vehicle/BatterySupplierMasterReport_AllPrint", {
      state: {
        finYearId,
        finYear,
        branchType: sessionBranchType,
        branchId: sessionBranchId,
        loginUserId: userId,
        filters,
        currentPage,
        itemsPerPage,
        totalRecords: rows.length,
        printScope: "CURRENT_PAGE",
      },
    });
  };

  const handlePrintRow = (row) => {
    const batteryId = pick(row, ["fld_batteryid", "batteryId", "BatteryId", "id"], 0);
    if (!batteryId) return toast.error("Battery ID not found");

    navigate("/vehicle/BatteryCardReport_Print", {
      state: {
        batteryId,
        finYearId,
        finYear,
        branchType: sessionBranchType,
        branchId: sessionBranchId,
        loginUserId: userId,
      },
    });
  };

  /* ---------------- pagination ---------------- */
  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = rows.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (page) => setCurrentPage(page);

  /* ---------------- columns mapping ---------------- */
  const getSupplierName = (r) =>
    pick(r, ["fld_SupplierName", "fld_suppliername", "SupplierName", "supplierName"], "-");

  const getSerialNo = (r) =>
    pick(r, ["fld_battery_serialno", "BatterySerialNo", "serialNo"], "-");

  const getPurchaseDate = (r) =>
    fmtDateDDMMYYYY(pick(r, ["fld_purchaseddate", "PurchaseDate", "purchasedDate"], ""));

  const getRate = (r) =>
    pick(r, ["fld_purchaseamt", "Rate", "rate"], "0.00");

  const getAmount = (r) =>
    pick(r, ["fld_purchaseamt", "PurchaseAmount", "purchaseAmt", "Amount"], "0.00");

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BATTERY CARD SUPPLIER DETAIL REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Battery Supplier Detail
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-700"
                }`}
              >
                INFRA SCREEN : B-50
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handlePrintCurrentPage}
                disabled={!rows.length}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } disabled:opacity-60`}
                title="Print only current page rows"
              >
                <FaPrint className="w-4 h-4" />
                Print
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Supplier Name</label>
                  <select
                    name="supplier"
                    value={filters.supplier}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {suppliers.map((s) => (
                      <option key={s.value ?? s.id ?? s.label} value={s.value ?? s.id ?? s.label}>
                        {s.label ?? s.name ?? s.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Battery Serial Number</label>
                  <input
                    name="serialNo"
                    value={filters.serialNo}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Type serial no..."
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    disabled={loading}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg disabled:opacity-60"
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg disabled:opacity-60`}
                  >
                    <FaSearch />
                    {loading ? "Loading..." : "Search Records"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {loading ? (
              <div className="text-center py-16">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  Loading...
                </h3>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Supplier Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Battery Serial No</th>
                      <th className="px-4 py-4 text-center font-semibold">Purchase Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Rate</th>
                      <th className="px-4 py-4 text-center font-semibold">Purchase Amount</th>
                      <th className="px-4 py-4 text-center font-semibold">Row Print</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map((r, idx) => (
                      <tr
                        key={pick(r, ["fld_batteryid", "batteryId", "id"], `${idx}`)}
                        className={`${isDark ? "bg-gray-800" : "bg-white"}`}
                      >
                        <td className={`px-4 py-4 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {indexOfFirstItem + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {getSupplierName(r)}
                        </td>
                        <td className={`px-4 py-4 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {getSerialNo(r)}
                        </td>
                        <td className={`px-4 py-4 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {getPurchaseDate(r) || "-"}
                        </td>
                        <td className={`px-4 py-4 text-right text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {formatMoney(getRate(r))}
                        </td>
                        <td className={`px-4 py-4 text-right text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {formatMoney(getAmount(r))}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handlePrintRow(r)}
                            className={`p-2 rounded ${
                              isDark
                                ? "bg-gray-700 text-white hover:bg-gray-600"
                                : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                            }`}
                            title="Print Battery Card"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, rows.length)} of {rows.length} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}